import { GetMyUpcomingReservationsResponseDto } from "@/api/generated/generatedTypes";
import { StyledActionButton } from "@/components/shared/StyledActionButton";
import { Box, Button, Flex, Group, Stack, Text } from "@mantine/core";
import { IconCircle, IconCircleDot, IconMessageCircle } from "@tabler/icons-react";
import { NavLink, useNavigate } from "react-router-dom";
import { ReservationRequestedLocationAndTimes } from "./ReservationRequestedLocationAndTimes";
import { ReservationStatusLabelResolver } from "./ReservationStatusLabelResolver";

interface ReservationListProps {
  reservations: GetMyUpcomingReservationsResponseDto[];
  onReservationSelect: (reservation: GetMyUpcomingReservationsResponseDto) => void;
  isPast?: boolean;
}

export function ReservationList({
  reservations,
  onReservationSelect,
  isPast = false,
}: ReservationListProps) {
  const navigate = useNavigate();

  return (
    <Stack gap="lg">
      {reservations.map((reservation) => {
        const isReservationPending = reservation.status === "PENDING";
        const isReservationConfirmed = reservation.status === "CONFIRMED";
        const isReservationCancelled =
          reservation.status === "CANCELLED" || reservation.status === "CANCELLED_NO_REFUND";
        const isReservationChangeRequested =
          reservation.status === "CHANGE_REQUESTED_BY_ADMIN" ||
          reservation.status === "PREFERENCE_OVERRIDE_REQUESTED";

        return (
          <Group key={reservation.id} wrap="nowrap" gap={0} align="center">
            {isReservationConfirmed ? (
              <IconCircleDot size={25} color="#FCD44C" style={{ marginRight: "8px" }} />
            ) : (
              <IconCircle size={25} color="#E0E0E0" style={{ marginRight: "8px" }} />
            )}
            <Box
              style={(theme) => ({
                flexGrow: 1,
                backgroundColor: "#202f37",
                borderRadius: theme.radius.lg,
                border: "1px solid #555555",
                padding: "0.5rem",
              })}
            >
              <Flex justify="flex-start" align="center" wrap="nowrap" mb="xs">
                <Flex align="flex-start" wrap="nowrap" flex={1} gap="xs">
                  <ReservationStatusLabelResolver reservation={reservation} isPast={isPast} />
                  <Stack gap={0} flex={1} pr="lg">
                    <ReservationRequestedLocationAndTimes reservation={reservation} />
                  </Stack>
                </Flex>
              </Flex>
              <Stack justify="center" align="center" gap="2px">
                {isReservationConfirmed && (
                  <StyledActionButton
                    onClick={() => onReservationSelect(reservation)}
                    size="sm"
                    textSize="1rem"
                    mb={isPast ? "xs" : undefined}
                  >
                    초대장 열기
                  </StyledActionButton>
                )}
                {isReservationChangeRequested && (
                  <StyledActionButton
                    onClick={() => navigate(`/reservation-change-request/${reservation.id}`)}
                    size="sm"
                    textSize="1rem"
                    buttonColor="#f896c7"
                    mb={isPast ? "xs" : undefined}
                  >
                    변경사항 확인하기
                  </StyledActionButton>
                )}

                {isReservationPending && (
                  <Text size="xs" c="#e3e5e7">
                    초대 확정이 되면 초대장을 열 수 있어요.
                  </Text>
                )}

                {!isReservationCancelled && !isPast && (
                  <Button
                    component={NavLink}
                    to={`/reservation-change/${reservation.id}`}
                    variant="subtle"
                  >
                    예약관리
                  </Button>
                )}

                {!isReservationCancelled && reservation.feedbackStatus === "NOT_SUBMITTED" && (
                  <Button
                    variant="subtle"
                    color="#f896c7"
                    leftSection={<IconMessageCircle size={16} />}
                    onClick={() =>
                      navigate(
                        `/home/reservation/past/${reservation.id}#event-detail-feedback-form`
                      )
                    }
                  >
                    피드백 남기기
                  </Button>
                )}
              </Stack>
            </Box>
          </Group>
        );
      })}
    </Stack>
  );
}
